/**
 * Data Layer - Cookie Storage
 * Enhanced cookie storage with app-specific helpers
 */

import { storageClient, type CookieOptions } from "@/shared/core/clients";
import { COOKIE_NAMES, TIMINGS, STORAGE_KEYS, LIMITS } from "@/constants";

/**
 * App-specific cookie storage utilities
 */
export const cookieStorage = {
  /**
   * Get auth token from cookies
   */
  getAuthToken(): string | undefined {
    return storageClient.getCookie(COOKIE_NAMES.AUTH_TOKEN);
  },

  /**
   * Set auth token in cookies
   */
  setAuthToken(token: string, rememberMe = false): void {
    const maxAge = rememberMe
      ? TIMINGS.COOKIE_EXPIRY_DAYS * 24 * 60 * 60
      : undefined;

    storageClient.setCookie(COOKIE_NAMES.AUTH_TOKEN, token, {
      maxAge,
      secure: true,
      sameSite: "Strict",
    });
  },

  /**
   * Remove auth token from cookies
   */
  removeAuthToken(): void {
    storageClient.deleteCookie(COOKIE_NAMES.AUTH_TOKEN);
  },

  /**
   * Check if user is authenticated (has token)
   */
  isAuthenticated(): boolean {
    return storageClient.hasCookie(COOKIE_NAMES.AUTH_TOKEN);
  },

  /**
   * Get user locale preference
   */
  getLocale(): string | undefined {
    return storageClient.getCookie(COOKIE_NAMES.LOCALE);
  },

  /**
   * Set user locale preference
   */
  setLocale(locale: string): void {
    storageClient.setCookie(COOKIE_NAMES.LOCALE, locale, {
      maxAge: TIMINGS.ONE_YEAR_SECONDS,
      sameSite: "Lax",
    });
  },

  /**
   * Get user theme preference
   */
  getTheme(): string | undefined {
    return storageClient.getCookie(COOKIE_NAMES.THEME);
  },

  /**
   * Set user theme preference
   */
  setTheme(theme: string): void {
    storageClient.setCookie(COOKIE_NAMES.THEME, theme, {
      maxAge: TIMINGS.ONE_YEAR_SECONDS,
      sameSite: "Lax",
    });
  },

  /**
   * Generic get/set methods (delegating to core storage client)
   */
  get: storageClient.getCookie,
  set: (name: string, value: string, options?: CookieOptions) =>
    storageClient.setCookie(name, value, options),
  delete: storageClient.deleteCookie,
  has: storageClient.hasCookie,
};

/**
 * App-specific localStorage utilities
 */
export const localStorageUtil = {
  /**
   * Get recent searches
   */
  getRecentSearches(): string[] {
    return storageClient.getLocalStorage<string[]>(
      STORAGE_KEYS.RECENT_SEARCHES,
      []
    );
  },

  /**
   * Add a recent search
   */
  addRecentSearch(search: string, maxItems = LIMITS.MAX_RECENT_SEARCHES): void {
    const searches = this.getRecentSearches();
    const filtered = searches.filter((s) => s !== search);
    const updated = [search, ...filtered].slice(0, maxItems);
    storageClient.setLocalStorage(STORAGE_KEYS.RECENT_SEARCHES, updated);
  },

  /**
   * Clear recent searches
   */
  clearRecentSearches(): void {
    storageClient.removeLocalStorage(STORAGE_KEYS.RECENT_SEARCHES);
  },

  /**
   * Generic get/set methods (delegating to core storage client)
   */
  get: storageClient.getLocalStorage,
  set: storageClient.setLocalStorage,
  remove: storageClient.removeLocalStorage,
};

/**
 * App-specific sessionStorage utilities
 */
export const sessionStorageUtil = {
  /**
   * Generic get/set methods (delegating to core storage client)
   */
  get: storageClient.getSessionStorage,
  set: storageClient.setSessionStorage,
  remove: storageClient.removeSessionStorage,
};

export default cookieStorage;
